import { ACTION_SUBJECT_PREFIX } from '../../constants/analytics';
import { type ProductKeys, is3pProductKey } from '../../constants/products';
import { type FireAnalyticsEvent } from '../../types';

import { type CommonAttributes, type NonPrivacySafeAttributes } from './types';

type RightPanelAppFilterBaseArgs = {
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
	productKey: ProductKeys | undefined;
	isConnected: boolean;
};

type RightPanelAppFilterHoveredArgs = RightPanelAppFilterBaseArgs & {
	index?: number;
	isInShowMore?: boolean;
};

/** button hovered searchPageRightPanelAppFilter */
export const onAppFilterOptionHovered = ({
	commonAttributes,
	nonPrivacySafeAttributes,
	productKey,
	isConnected,
	index,
	isInShowMore,
}: RightPanelAppFilterHoveredArgs): FireAnalyticsEvent => ({
	eventType: 'ui',
	actionSubject: 'button',
	actionSubjectId: `${ACTION_SUBJECT_PREFIX}RightPanelAppFilter`,
	action: 'hovered',
	attributes: {
		additionalAttributes: {
			productKey: productKey ?? 'all',
			is3pProduct: productKey ? is3pProductKey(productKey) : false,
			isConnected,
			...(index !== undefined && { index }),
			...(isInShowMore !== undefined && { isInShowMore }),
		},
		commonAttributes,
	},
	nonPrivacySafeAttributes,
});

/** button hovered searchPageRightPanelAppFilterMenuButton */
export const onAppFilterMenuButtonHovered = ({
	commonAttributes,
	nonPrivacySafeAttributes,
	productKey,
	isConnected,
}: RightPanelAppFilterBaseArgs): FireAnalyticsEvent => ({
	eventType: 'ui',
	actionSubject: 'button',
	actionSubjectId: `${ACTION_SUBJECT_PREFIX}RightPanelAppFilterMenuButton`,
	action: 'hovered',
	attributes: {
		additionalAttributes: {
			productKey: productKey ?? 'all',
			is3pProduct: productKey ? is3pProductKey(productKey) : false,
			isConnected,
		},
		commonAttributes,
	},
	nonPrivacySafeAttributes,
});

/** button clicked searchPageRightPanelAppFilterShowMore */
export const onAppFilterShowMoreClicked = ({
	commonAttributes,
	nonPrivacySafeAttributes,
	isConnected,
}: {
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
	isConnected: boolean;
}): FireAnalyticsEvent => ({
	eventType: 'ui',
	actionSubject: 'button',
	actionSubjectId: `${ACTION_SUBJECT_PREFIX}RightPanelAppFilterShowMore`,
	action: 'clicked',
	attributes: {
		additionalAttributes: {
			isConnected,
		},
		commonAttributes,
	},
	nonPrivacySafeAttributes,
});

/** button clicked searchPageRightPanelAppFilterHideButton */
export const onAppFilterHideButtonClicked = ({
	commonAttributes,
	nonPrivacySafeAttributes,
	productKey,
	isConnected,
}: RightPanelAppFilterBaseArgs): FireAnalyticsEvent => ({
	eventType: 'ui',
	actionSubject: 'button',
	actionSubjectId: `${ACTION_SUBJECT_PREFIX}RightPanelAppFilterHideButton`,
	action: 'clicked',
	attributes: {
		additionalAttributes: {
			productKey: productKey ?? 'all',
			is3pProduct: productKey ? is3pProductKey(productKey) : false,
			isConnected,
		},
		commonAttributes,
	},
	nonPrivacySafeAttributes,
});

/** button hovered searchPageRightPanelAppFilterHideButton */
export const onAppFilterHideButtonHovered = ({
	commonAttributes,
	nonPrivacySafeAttributes,
	productKey,
	isConnected,
}: RightPanelAppFilterBaseArgs): FireAnalyticsEvent => ({
	eventType: 'ui',
	actionSubject: 'button',
	actionSubjectId: `${ACTION_SUBJECT_PREFIX}RightPanelAppFilterHideButton`,
	action: 'hovered',
	attributes: {
		additionalAttributes: {
			productKey: productKey ?? 'all',
			is3pProduct: productKey ? is3pProductKey(productKey) : false,
			isConnected,
		},
		commonAttributes,
	},
	nonPrivacySafeAttributes,
});

/** rendered searchPageRightPanelAppFilter */
export const onAppFilterRendered = ({
	commonAttributes,
	nonPrivacySafeAttributes,
	numOfFilters,
	numOfUnconnectedApps,
	viewportHeight,
	viewportWidth,
}: {
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
	numOfFilters: number;
	numOfUnconnectedApps: number;
	viewportHeight?: number;
	viewportWidth?: number;
}): FireAnalyticsEvent => ({
	eventType: 'ui',
	actionSubject: 'filter',
	actionSubjectId: `${ACTION_SUBJECT_PREFIX}RightPanelAppFilter`,
	action: 'rendered',
	attributes: {
		additionalAttributes: {
			numOfFilters,
			numOfUnconnectedApps,
			...(viewportHeight !== undefined && { viewportHeight }),
			...(viewportWidth !== undefined && { viewportWidth }),
		},
		commonAttributes,
	},
	nonPrivacySafeAttributes,
});

/** button clicked searchPageRightPanelFeedback */
export const onRightPanelFeedbackClicked = ({
	commonAttributes,
	nonPrivacySafeAttributes,
}: {
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
}): FireAnalyticsEvent => ({
	eventType: 'ui',
	actionSubject: 'button',
	actionSubjectId: `${ACTION_SUBJECT_PREFIX}RightPanelFeedback`,
	action: 'clicked',
	attributes: {
		additionalAttributes: {},
		commonAttributes,
	},
	nonPrivacySafeAttributes,
});

/** button clicked searchPageRightPanelFeedbackModalClose */
export const onRightPanelFeedbackModalCloseClicked = ({
	commonAttributes,
	nonPrivacySafeAttributes,
}: {
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
}): FireAnalyticsEvent => ({
	eventType: 'ui',
	actionSubject: 'button',
	actionSubjectId: `${ACTION_SUBJECT_PREFIX}RightPanelFeedbackModalClose`,
	action: 'clicked',
	attributes: {
		additionalAttributes: {},
		commonAttributes,
	},
	nonPrivacySafeAttributes,
});

/** textField focused searchPageRightPanelAppFilterShowMore */
export const onAppFilterShowMoreTextFieldFocused = ({
	commonAttributes,
	nonPrivacySafeAttributes,
}: {
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
}): FireAnalyticsEvent => ({
	eventType: 'ui',
	actionSubject: 'textField',
	actionSubjectId: `${ACTION_SUBJECT_PREFIX}RightPanelAppFilterShowMore`,
	action: 'focused',
	attributes: {
		additionalAttributes: {},
		commonAttributes,
	},
	nonPrivacySafeAttributes,
});
